/**
 * Bonsai Deadlines & Tasks — Fee Earner View (v2)
 * Full table with filters, side panel, add deadline form.
 */
import { useEffect, useState, useCallback } from "react";
import { useBonsai } from "../../contexts/BonsaiContext";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import {
  CalendarClock, CheckCircle2, Clock, AlertTriangle, Plus,
  Trash2, Loader2, ArrowLeft, X, ChevronRight, Search,
  Shield, Calendar, ExternalLink, Filter
} from "lucide-react";
import { format, isPast, isToday, differenceInDays, parseISO } from "date-fns";
import { fmtUK } from "../../lib/dateUtils";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const TYPE_LABELS = {
  court_tribunal: "Court / Tribunal",
  statutory: "Statutory",
  client_instruction: "Client",
  internal: "Internal",
  other: "Other",
};
const TYPE_COLORS = {
  court_tribunal: "bg-status-red-dim text-status-red border-red-200",
  statutory: "bg-status-amber-dim text-status-amber border-amber-200",
  client_instruction: "bg-blue-100 text-blue-700 border-blue-200",
  internal: "bg-slate-100 text-slate-600 border-navy-border",
  other: "bg-gray-100 text-gray-600 border-gray-200",
};
const STATUS_COLORS = {
  open: "bg-teal-dim text-teal",
  completed: "bg-status-green-dim text-status-green",
  adjourned: "bg-yellow-100 text-yellow-700",
  cancelled: "bg-slate-100 text-slate-500",
};

function dueLabel(dateStr) {
  if (!dateStr) return "";
  const d = parseISO(dateStr);
  if (isToday(d)) return "Today";
  const diff = differenceInDays(d, new Date());
  if (diff === 1) return "Tomorrow";
  if (diff < 0) return `${Math.abs(diff)}d overdue`;
  if (diff <= 7) return `in ${diff}d`;
  return format(d, "d MMM yyyy");
}

function urgencyClass(dateStr, status) {
  if (status !== "open") return "text-slate-400";
  const d = parseISO(dateStr);
  if (isPast(d) && !isToday(d)) return "text-status-red font-bold";
  if (isToday(d)) return "text-status-amber font-semibold";
  if (differenceInDays(d, new Date()) <= 3) return "text-orange-600 font-medium";
  return "text-txt";
}

export default function BonsaiDeadlines() {
  const { loading, authenticated, bonsaiApi } = useBonsai();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState("open");
  const [typeFilter, setTypeFilter] = useState("");
  const [highRiskOnly, setHighRiskOnly] = useState(false);
  const [dateRange, setDateRange] = useState("");
  const [matterSearch, setMatterSearch] = useState("");

  // Side panel
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const fetchTasks = useCallback(async () => {
    if (!authenticated) return;
    setDataLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (typeFilter) params.set("deadline_type", typeFilter);
      if (highRiskOnly) params.set("high_risk", "true");
      if (dateRange) params.set("date_range", dateRange);
      if (matterSearch) params.set("matter_reference", matterSearch);
      const res = await bonsaiApi.get(`/deadlines/tasks?${params.toString()}`);
      setTasks(res.data.tasks || []);
    } catch (e) {
      console.error("Failed to load deadlines:", e);
    }
    setDataLoading(false);
  }, [authenticated, bonsaiApi, statusFilter, typeFilter, highRiskOnly, dateRange, matterSearch]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const handleComplete = async (id) => {
    try {
      await bonsaiApi.put(`/deadlines/tasks/${id}`, { status: "completed" });
      toast.success("Marked as completed");
      fetchTasks();
      if (selected?.deadline_id === id || selected?.id === id) setSelected(null);
    } catch { toast.error("Failed to update"); }
  };

  const handleDelete = async (id) => {
    try {
      await bonsaiApi.delete(`/deadlines/tasks/${id}`);
      toast.success("Deleted");
      fetchTasks();
      if (selected?.deadline_id === id || selected?.id === id) setSelected(null);
    } catch { toast.error("Failed to delete"); }
  };

  const handleSyncOutlook = async (id) => {
    try {
      await bonsaiApi.post(`/deadlines/tasks/${id}/sync-outlook`);
      toast.success("Synced to Outlook calendar");
      fetchTasks();
    } catch { toast.error("Outlook sync failed"); }
  };

  if (loading) return <div className="p-8 text-center text-slate-400">Loading...</div>;
  if (!authenticated) return <div className="p-8 text-center text-slate-400">Please sign in to view deadlines.</div>;

  const taskId = (t) => t.deadline_id || t.id;

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      <div className="max-w-7xl mx-auto p-6" data-testid="deadlines-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <button onClick={() => navigate("/bonsai")} className="flex items-center gap-1 text-xs text-slate-500 hover:text-txt mb-1" data-testid="back-to-bonsai">
              <ArrowLeft className="w-3 h-3" /> Bonsai Mail
            </button>
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-blue-600" />
              Deadlines & Tasks
            </h1>
          </div>
          <Button onClick={() => { setShowAdd(true); setSelected(null); }} className="gap-1.5" data-testid="add-deadline-btn">
            <Plus className="w-4 h-4" /> Add deadline
          </Button>
        </div>

        {/* Filters bar */}
        <div className="flex flex-wrap items-center gap-2 mb-4 bg-white p-3 rounded-md border border-navy-border" data-testid="filters-bar">
          <Filter className="w-4 h-4 text-slate-400" />
          {/* Status */}
          {["open", "completed", "adjourned", "cancelled", "all"].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${statusFilter === s ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
              data-testid={`filter-status-${s}`}>
              {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
          <span className="w-px h-5 bg-slate-200" />
          {/* Date range */}
          <select value={dateRange} onChange={e => setDateRange(e.target.value)}
            className="text-xs border border-navy-border rounded px-2 py-1 bg-white" data-testid="filter-date-range">
            <option value="">All dates</option>
            <option value="today">Today</option>
            <option value="7days">Next 7 days</option>
            <option value="30days">Next 30 days</option>
          </select>
          {/* Type */}
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
            className="text-xs border border-navy-border rounded px-2 py-1 bg-white" data-testid="filter-type">
            <option value="">All types</option>
            {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          {/* High risk */}
          <label className="flex items-center gap-1 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={highRiskOnly} onChange={e => setHighRiskOnly(e.target.checked)} className="rounded border-slate-300" data-testid="filter-high-risk" />
            High-risk only
          </label>
          {/* Matter search */}
          <div className="relative">
            <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={matterSearch} onChange={e => setMatterSearch(e.target.value)} placeholder="Matter ref..."
              className="h-7 text-xs pl-6 w-32" data-testid="filter-matter-search" />
          </div>
        </div>

        <div className="flex gap-5">
          {/* Main table */}
          <div className={`flex-1 ${selected || showAdd ? "max-w-[60%]" : ""}`}>
            {dataLoading ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : tasks.length === 0 ? (
              <Card className="border-dashed border-slate-300">
                <CardContent className="py-16 text-center">
                  <CalendarClock className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500 text-sm">No deadlines match your filters.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="bg-white rounded-md border border-navy-border overflow-hidden">
                <table className="w-full text-sm" data-testid="deadlines-table">
                  <thead>
                    <tr className="border-b border-navy-border bg-navy-hover/60">
                      <th className="text-left px-3 py-2 text-xs font-medium text-slate-500">Due</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-slate-500">Matter</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-slate-500">Client</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-slate-500">Type</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-slate-500">Summary</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-slate-500">Status</th>
                      <th className="px-3 py-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {tasks.map((t) => {
                      const id = taskId(t);
                      const isActive = (selected && taskId(selected) === id);
                      return (
                        <tr key={id}
                          className={`border-b border-navy-border cursor-pointer transition-colors ${isActive ? "bg-blue-50" : "hover:bg-navy-hover/60"} ${t.high_risk_flag && t.status === "open" ? "border-l-2 border-l-red-400" : ""}`}
                          onClick={() => { setSelected(t); setShowAdd(false); }}
                          data-testid={`deadline-row-${id}`}>
                          <td className={`px-3 py-2.5 whitespace-nowrap ${urgencyClass(t.deadline_date, t.status)}`}>
                            <div className="text-xs">{t.deadline_date ? fmtUK(t.deadline_date, "d MMM ''yy") : "—"}</div>
                            <div className="text-[10px] opacity-70">{t.deadline_time || ""} {dueLabel(t.deadline_date)}</div>
                          </td>
                          <td className="px-3 py-2.5 text-xs text-txt max-w-[120px] truncate">{t.matter_reference || "—"}</td>
                          <td className="px-3 py-2.5 text-xs text-slate-600 max-w-[100px] truncate">{t.client_name || "—"}</td>
                          <td className="px-3 py-2.5">
                            <Badge className={`${TYPE_COLORS[t.deadline_type] || TYPE_COLORS.other} text-[10px] px-1.5 py-0`}>
                              {TYPE_LABELS[t.deadline_type] || t.deadline_type || "Other"}
                            </Badge>
                          </td>
                          <td className="px-3 py-2.5 text-xs text-txt max-w-[180px] truncate">
                            {t.high_risk_flag && <Shield className="w-3 h-3 text-status-red inline mr-1" />}
                            {t.escalation_required && <AlertTriangle className="w-3 h-3 text-status-amber inline mr-1" />}
                            {t.summary || t.description || "—"}
                          </td>
                          <td className="px-3 py-2.5">
                            <Badge className={`${STATUS_COLORS[t.status] || STATUS_COLORS.open} text-[10px] px-1.5 py-0`}>
                              {t.status || "open"}
                            </Badge>
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            {t.synced_to_outlook && <Calendar className="w-3 h-3 text-blue-400 inline" title="Synced to Outlook" />}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Side panel — View/Edit */}
          {selected && !showAdd && (
            <SidePanel task={selected} onClose={() => setSelected(null)}
              onComplete={handleComplete} onDelete={handleDelete}
              onSyncOutlook={handleSyncOutlook} onUpdated={fetchTasks}
              bonsaiApi={bonsaiApi} />
          )}

          {/* Side panel — Add deadline */}
          {showAdd && (
            <AddDeadlinePanel onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); fetchTasks(); }}
              bonsaiApi={bonsaiApi} />
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Side Panel — View/Edit a deadline
// ============================================================

function SidePanel({ task, onClose, onComplete, onDelete, onSyncOutlook, onUpdated, bonsaiApi }) {
  const id = task.deadline_id || task.id;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    setEditing(false);
    setForm({
      summary: task.summary || task.description || "",
      detail: task.detail || "",
      deadline_date: task.deadline_date || "",
      deadline_time: task.deadline_time || "",
      deadline_type: task.deadline_type || "other",
      high_risk_flag: task.high_risk_flag || false,
      escalation_required: task.escalation_required || false,
      notes_for_management: task.notes_for_management || "",
    });
  }, [task]);

  const handleSave = async () => {
    try {
      await bonsaiApi.put(`/deadlines/tasks/${id}`, form);
      toast.success("Deadline updated");
      setEditing(false);
      onUpdated();
    } catch { toast.error("Failed to save"); }
  };

  return (
    <div className="w-[340px] flex-shrink-0 bg-white border border-navy-border rounded-md overflow-hidden" data-testid="deadline-side-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-navy-border bg-navy-hover/60">
        <h3 className="text-sm font-semibold text-txt">Deadline details</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
      </div>
      <div className="p-4 space-y-3 text-sm max-h-[70vh] overflow-y-auto">
        {editing ? (
          <>
            <Field label="Summary"><Input value={form.summary} onChange={e => setForm(f => ({...f, summary: e.target.value}))} className="h-8 text-xs" /></Field>
            <Field label="Detail"><textarea value={form.detail} onChange={e => setForm(f => ({...f, detail: e.target.value}))} className="w-full text-xs border border-navy-border rounded p-2 h-20 resize-none" /></Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Date"><Input type="date" value={form.deadline_date} onChange={e => setForm(f => ({...f, deadline_date: e.target.value}))} className="h-8 text-xs" /></Field>
              <Field label="Time"><Input type="time" value={form.deadline_time} onChange={e => setForm(f => ({...f, deadline_time: e.target.value}))} className="h-8 text-xs" /></Field>
            </div>
            <Field label="Type">
              <select value={form.deadline_type} onChange={e => setForm(f => ({...f, deadline_type: e.target.value}))} className="w-full text-xs border border-navy-border rounded px-2 py-1.5">
                {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.high_risk_flag} onChange={e => setForm(f => ({...f, high_risk_flag: e.target.checked}))} /> High-risk</label>
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.escalation_required} onChange={e => setForm(f => ({...f, escalation_required: e.target.checked}))} /> Escalation required</label>
            {form.escalation_required && (
              <Field label="Notes for management"><textarea value={form.notes_for_management} onChange={e => setForm(f => ({...f, notes_for_management: e.target.value}))} className="w-full text-xs border border-navy-border rounded p-2 h-16 resize-none" /></Field>
            )}
            <div className="flex gap-2 pt-2">
              <Button size="sm" onClick={handleSave} data-testid="save-deadline-btn">Save</Button>
              <Button size="sm" variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
            </div>
          </>
        ) : (
          <>
            <div>
              <p className="text-base font-semibold text-white">{task.summary || task.description}</p>
              {task.detail && <p className="text-xs text-slate-500 mt-1">{task.detail}</p>}
            </div>
            <div className="grid grid-cols-2 gap-y-2 text-xs">
              <span className="text-slate-400">Due</span>
              <span className={urgencyClass(task.deadline_date, task.status)}>
                {task.deadline_date ? fmtUK(task.deadline_date, "EEE d MMM yyyy") : "—"} {task.deadline_time || ""}
              </span>
              <span className="text-slate-400">Matter</span>
              <span className="text-blue-600">{task.matter_reference || "—"}</span>
              <span className="text-slate-400">Client</span>
              <span>{task.client_name || "—"}</span>
              <span className="text-slate-400">Type</span>
              <Badge className={`${TYPE_COLORS[task.deadline_type] || TYPE_COLORS.other} text-[10px] w-fit`}>{TYPE_LABELS[task.deadline_type] || "Other"}</Badge>
              <span className="text-slate-400">Status</span>
              <Badge className={`${STATUS_COLORS[task.status] || STATUS_COLORS.open} text-[10px] w-fit`}>{task.status}</Badge>
              <span className="text-slate-400">High-risk</span>
              <span>{task.high_risk_flag ? <Shield className="w-3.5 h-3.5 text-status-red inline" /> : "No"}</span>
              <span className="text-slate-400">Responsible</span>
              <span>{task.responsible_user_name || task.user_id || "—"}</span>
              <span className="text-slate-400">Source</span>
              <span>{task.source === "email_extracted" ? "Email" : task.source === "manual_entry" ? "Manual" : task.source || "—"}</span>
              {task.synced_to_outlook && <><span className="text-slate-400">Calendar</span><span className="text-blue-500 flex items-center gap-1"><Calendar className="w-3 h-3" />Synced</span></>}
              {task.created_by_user_name && <><span className="text-slate-400">Created by</span><span>{task.created_by_user_name}</span></>}
            </div>
            {task.escalation_required && task.notes_for_management && (
              <div className="bg-status-amber-dim border border-amber-200 rounded p-2 text-xs">
                <span className="font-medium text-status-amber">Escalation note:</span> {task.notes_for_management}
              </div>
            )}
            <div className="flex flex-wrap gap-1.5 pt-2 border-t border-navy-border">
              <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="edit-deadline-btn">Edit</Button>
              {task.status === "open" && (
                <Button size="sm" variant="outline" className="text-status-green border-green-300 hover:bg-status-green-dim"
                  onClick={() => onComplete(id)} data-testid="complete-deadline-btn">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Done
                </Button>
              )}
              {!task.synced_to_outlook && task.status === "open" && (
                <Button size="sm" variant="outline" className="text-blue-700 border-blue-300 hover:bg-blue-50"
                  onClick={() => onSyncOutlook(id)} data-testid="sync-outlook-btn">
                  <Calendar className="w-3.5 h-3.5 mr-1" /> Add to calendar
                </Button>
              )}
              {task.matter_reference && (
                <Button size="sm" variant="ghost" className="text-slate-500" onClick={() => {}}>
                  <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open matter
                </Button>
              )}
              <Button size="sm" variant="ghost" className="text-status-red hover:text-status-red ml-auto"
                onClick={() => onDelete(id)} data-testid="delete-deadline-btn">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5 block">{label}</label>{children}</div>;
}

// ============================================================
// Add Deadline Panel
// ============================================================

function AddDeadlinePanel({ onClose, onCreated, bonsaiApi, matterRef: initialMatter }) {
  const [form, setForm] = useState({
    summary: "",
    detail: "",
    deadline_date: "",
    deadline_time: "",
    deadline_type: "other",
    high_risk_flag: false,
    matter_reference: initialMatter || "",
    responsible_user_name: "",
    escalation_required: false,
    notes_for_management: "",
    sync_to_outlook: false,
    source: "manual_entry",
  });
  const [saving, setSaving] = useState(false);
  const [feeEarners, setFeeEarners] = useState([]);

  useEffect(() => {
    bonsaiApi.get("/deadlines/fee-earners").then(r => setFeeEarners(r.data.fee_earners || [])).catch(() => {});
  }, [bonsaiApi]);

  // When matter changes, load linked fee earners
  useEffect(() => {
    if (form.matter_reference && form.matter_reference.length >= 4) {
      bonsaiApi.get(`/deadlines/fee-earners/matter/${encodeURIComponent(form.matter_reference)}`)
        .then(r => { if (r.data.fee_earners?.length) setFeeEarners(r.data.fee_earners); })
        .catch(() => {});
    }
  }, [form.matter_reference, bonsaiApi]);

  const handleSubmit = async () => {
    if (!form.deadline_date || !form.summary) { toast.error("Summary and date are required"); return; }
    setSaving(true);
    try {
      await bonsaiApi.post("/deadlines/tasks", form);
      toast.success("Deadline created");
      onCreated();
    } catch (e) {
      toast.error("Failed to create deadline");
    }
    setSaving(false);
  };

  return (
    <div className="w-[340px] flex-shrink-0 bg-white border border-navy-border rounded-md overflow-hidden" data-testid="add-deadline-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-navy-border bg-blue-50">
        <h3 className="text-sm font-semibold text-blue-700">Add deadline</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
      </div>
      <div className="p-4 space-y-3 text-sm max-h-[70vh] overflow-y-auto">
        <Field label="Summary *"><Input value={form.summary} onChange={e => setForm(f => ({...f, summary: e.target.value}))} placeholder="File defence, Court hearing..." className="h-8 text-xs" data-testid="add-summary" /></Field>
        <Field label="Detail"><textarea value={form.detail} onChange={e => setForm(f => ({...f, detail: e.target.value}))} className="w-full text-xs border border-navy-border rounded p-2 h-16 resize-none" placeholder="Context, order wording..." /></Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Date *"><Input type="date" value={form.deadline_date} onChange={e => setForm(f => ({...f, deadline_date: e.target.value}))} className="h-8 text-xs" data-testid="add-date" /></Field>
          <Field label="Time"><Input type="time" value={form.deadline_time} onChange={e => setForm(f => ({...f, deadline_time: e.target.value}))} className="h-8 text-xs" /></Field>
        </div>
        <Field label="Type">
          <select value={form.deadline_type} onChange={e => {
            const v = e.target.value;
            setForm(f => ({...f, deadline_type: v, high_risk_flag: v === "court_tribunal" || v === "statutory"}));
          }} className="w-full text-xs border border-navy-border rounded px-2 py-1.5" data-testid="add-type">
            {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
        <Field label="Matter reference"><Input value={form.matter_reference} onChange={e => setForm(f => ({...f, matter_reference: e.target.value}))} placeholder="BL.ABC.001.0001" className="h-8 text-xs" data-testid="add-matter" /></Field>
        <Field label="Responsible fee earner">
          <select value={form.responsible_user_name} onChange={e => setForm(f => ({...f, responsible_user_name: e.target.value}))}
            className="w-full text-xs border border-navy-border rounded px-2 py-1.5" data-testid="add-responsible">
            <option value="">Current user (default)</option>
            {feeEarners.map(fe => (
              <option key={fe.name} value={fe.name}>
                {fe.name} ({fe.initials}) — {fe.department}{fe.is_primary ? " [Primary]" : ""}{fe.is_supervisor ? " [Supervisor]" : ""}
              </option>
            ))}
          </select>
        </Field>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={form.high_risk_flag} onChange={e => setForm(f => ({...f, high_risk_flag: e.target.checked}))} className="rounded" /> High-risk</label>
          <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={form.escalation_required} onChange={e => setForm(f => ({...f, escalation_required: e.target.checked}))} className="rounded" /> Escalate</label>
        </div>
        <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={form.sync_to_outlook} onChange={e => setForm(f => ({...f, sync_to_outlook: e.target.checked}))} className="rounded" /> Add to Outlook calendar</label>
        <div className="pt-2 border-t border-navy-border">
          <Button onClick={handleSubmit} disabled={saving} className="w-full" data-testid="submit-deadline-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create deadline"}
          </Button>
        </div>
      </div>
    </div>
  );
}
